import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ROLE_LABELS } from "@/lib/stages";

/**
 * Edit an existing user. Email is immutable on the backend; we display it as read-only.
 * Role cannot be changed if `isSelf` is true (backend enforces).
 * Password is optional — leave blank to keep current password.
 */
export default function EditUserDialog({ open, onClose, user, isSelf, onSaved }) {
  const [name, setName] = useState(user?.name || "");
  const [role, setRole] = useState(user?.role || "builder");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset form fields when the dialog opens for a different user
  React.useEffect(() => {
    if (open) {
      setName(user?.name || "");
      setRole(user?.role || "builder");
      setPassword("");
      setShowPassword(false);
      setError("");
    }
  }, [open, user]);

  if (!user) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    const payload = {};
    if (name.trim() && name.trim() !== user.name) payload.name = name.trim();
    if (!isSelf && role && role !== user.role) payload.role = role;
    if (showPassword && password.trim()) {
      if (password.trim().length < 6) {
        setError("Password must be at least 6 characters.");
        return;
      }
      payload.password = password.trim();
    }

    if (Object.keys(payload).length === 0) {
      setError("Nothing to save — change a field first.");
      return;
    }

    setSaving(true);
    try {
      await api.patch(`/users/${user.id}`, payload);
      onSaved?.();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit user</DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Email</Label>
            <Input
              value={user.email}
              disabled
              readOnly
              className="bg-zinc-50 text-zinc-500 font-mono"
              data-testid="edit-user-email-readonly"
            />
            <p className="text-[10px] text-zinc-500 mt-1">Email cannot be changed.</p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Full name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              data-testid="edit-user-name"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Role</Label>
            <Select value={role} onValueChange={setRole} disabled={isSelf}>
              <SelectTrigger data-testid="edit-user-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(ROLE_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isSelf && (
              <p className="text-[10px] text-zinc-500 mt-1">
                You cannot change your own role. Ask another admin to do this.
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Password</Label>
              <button
                type="button"
                onClick={() => { setShowPassword(!showPassword); setPassword(""); }}
                className="text-[10px] uppercase tracking-widest text-[#C8102E] hover:underline font-bold"
                data-testid="toggle-password-field"
              >
                {showPassword ? "Cancel reset" : "Reset password"}
              </button>
            </div>
            {showPassword ? (
              <>
                <Input
                  type="text"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="New temporary password (min 6 chars)"
                  minLength={6}
                  required
                  className="mt-1"
                  data-testid="edit-user-new-password"
                />
                <p className="text-[10px] text-zinc-500 mt-1">
                  Will be sent to {user.email}. Share it securely with them.
                </p>
              </>
            ) : (
              <p className="text-[10px] text-zinc-500 mt-1">Leave unchanged.</p>
            )}
          </div>

          {error && (
            <div className="text-sm bg-red-50 text-red-700 border border-red-200 rounded-md px-3 py-2" data-testid="edit-user-error">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="cancel-edit-user">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#C8102E] hover:bg-[#A00D25]"
              data-testid="submit-edit-user"
            >
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
